import React from 'react';
import { Field, Form } from 'formik';
// Material UI
import FormControl from '@material-ui/core/FormControl';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import List from '@material-ui/core/List';

// Data
import { getMessages } from './create-drill.messages';
import { warnTextColor } from '../../../lib/theme';
import { ICreateDrill, ISkillSetFields } from './create-drill.interface';
import { shouldProceedWithSkillSetRemoval, toServerSkillSets } from './create-drill.helpers';

// Components
import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import { FormikSelect } from '../../_core/Formik/formik-select.component';
import { FormikInput } from '../../_core/Formik/formik-input.component';
import { FormHelperText } from '../../_core/_ui/forms.component';
import { OutlineButton, PrimaryButton } from '../../_core/_ui/buttons.component';
import { FormikCheckbox } from '../../_core/Formik/formik-checkbox.component';
import { SectionWrapper } from '../../_core/_ui/structure.components';
import { SectionHeading } from '../../_core/_ui/typography.component';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import IconButton from '@material-ui/core/IconButton';
import CircularProgress from '@material-ui/core/CircularProgress';
import DeleteIcon from '@material-ui/icons/Delete';
import { useCompanySportTerminology } from '../../../lib/stores/company.store';
import { SportTerminology } from '../../../lib/types';
import { pluralize, summarizeList } from '../../../lib/helpers/text.helpers';


export const CreateDrill: React.FC<ICreateDrill> = ({
  isEditing,
  form,
  checkConformity,
  submitError,
  showSaved,
  danglingVideoFilenames,
  skillOptions,
  positionOptions,
  loading,
  fetchError,
  isAdmin
}) => {
  const termLookup = useCompanySportTerminology()
  const messages = getMessages(termLookup);

  const { values, isSubmitting, isValid, setFieldValue } = form;

  const [removingIndex, setRemovingIndex] = React.useState<number | null>(null);

  const danglingDescriptor = pluralize(danglingVideoFilenames.length, 'video references a skill', 'videos reference skills');
  const danglingFilenames = summarizeList(danglingVideoFilenames, 10);

  if (fetchError) return <Box mt={2}><Typography><IntlMsg {...messages.errFetch} /></Typography></Box>;

  // Gate the removal on the calibration videos it would orphan: preview against the post-removal
  // skillSets and confirm before dropping the set. See shouldProceedWithSkillSetRemoval.
  const removeSkillSet = async (index: number) => {
    const nextSkillSets = values.skillSets.filter((_skillSet, i) => i !== index);

    let dangling: string[] = [];
    setRemovingIndex(index);
    try {
      dangling = await checkConformity(toServerSkillSets(nextSkillSets));
    } finally {
      setRemovingIndex(null);
    }

    const terms = { metric: termLookup[SportTerminology.METRIC], event: termLookup[SportTerminology.EVENT] };
    if (!shouldProceedWithSkillSetRemoval(dangling, terms)) return;

    setFieldValue('skillSets', nextSkillSets);
  };

  return (
    <Form>
      <Field fid="cdf" name="drillName" component={FormikInput} label={messages.lblName} />
     <Box>
        <SectionWrapper dark padding="8px 24px">
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <SectionHeading>{termLookup[SportTerminology.METRIC]} Sets</SectionHeading>
              <List>
                {values.skillSets.map((skillSet: ISkillSetFields, index: number) => {
                  if (index === values.skillSets.length - 1) return <></>;

                  const positionNames = skillSet?.positionIds?.map(position => position.label).join(', ');
                  const skillNames = skillSet?.skillIds?.map(skill => skill.label).join(', ');

                  return (
                    <ListItem key={index}>
                      <ListItemText primary={skillNames} secondary={positionNames} />
                      <ListItemSecondaryAction>
                        <IconButton edge="end" aria-label="delete" disabled={removingIndex !== null} onClick={() => removeSkillSet(index)}>
                          {removingIndex === index ? <CircularProgress size={20} /> : <DeleteIcon />}
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  );
                })}
                {values.skillSets.length <= 1 && <ListItem><ListItemText primary="None" /></ListItem>}
              </List>
            </Grid>
            <Grid item xs={12} md={6}>
              <Field
                fid="cpf"
                name={`skillSets.${values.skillSets.length - 1}.positionIds`}
                component={FormikSelect}
                label={messages.lblPosition}
                options={positionOptions.filter(({active}) => active)}
                isLoading={loading}
                isMulti
              />
              <Field
                fid="cdf"
                name={`skillSets.${values.skillSets.length - 1}.skillIds`}
                component={FormikSelect}
                label={messages.lblSkills}
                options={skillOptions}
                isLoading={loading}
                isMulti
              />
              <FormControl margin="dense">
                <OutlineButton
                  onClick={() => setFieldValue('skillSets', [...values.skillSets, { positionIds: [], skillIds: [] }])}
                >Add {termLookup[SportTerminology.METRIC]} Set</OutlineButton>
              </FormControl>
            </Grid>
          </Grid>
        </SectionWrapper>
      </Box>
      {isAdmin && !isEditing && (
        <Field fid="cdf" name="isGlobal" component={FormikCheckbox} label={messages.lblGlobal} />
      )}

      <FormControl margin="dense">
        <Box>
          <PrimaryButton disabled={isSubmitting || !isValid} type="submit">
            {isSubmitting ? <IntlMsg {...messages.btnSubmitting} /> : <IntlMsg {...isEditing ? messages.btnEdit : messages.btnSubmit} />}
          </PrimaryButton>
        </Box>
        {submitError && <FormHelperText error>{submitError}</FormHelperText>}
        {showSaved && <FormHelperText className="success"><IntlMsg {...(isEditing ? messages.msgSaved : messages.msgCreated)} /></FormHelperText>}
        {danglingVideoFilenames.length > 0 && (
          <FormHelperText style={{ color: warnTextColor }}>
            {danglingVideoFilenames.length} calibration {danglingDescriptor} no longer in this {termLookup[SportTerminology.EVENT].toLowerCase()} and will not be used until re-calibrated: {danglingFilenames}.
          </FormHelperText>
        )}
      </FormControl>
    </Form>
  );
}
